import type { GetAuthToken } from "@/lib/auth-context";
import type { AppRouter } from "@my-second-brain/api/router";
import { createTRPCReact, httpBatchLink } from "@trpc/react-query";
import superjson from "superjson";

export type { AppRouter };

/**
 * Typed tRPC React hooks.
 *
 * Usage:
 *   const { data } = trpc.ingest.stats.useQuery()
 *   const mutation = trpc.distill.commit.useMutation()
 */
export const trpc = createTRPCReact<AppRouter>();

export async function signRequest(secret: string, method: string, path: string, body: string) {
	const timestamp = Date.now().toString();
	const message = `${timestamp}:${method}:${path}:${body}`;
	const key = await crypto.subtle.importKey(
		"raw",
		new TextEncoder().encode(secret),
		{ name: "HMAC", hash: "SHA-256" },
		false,
		["sign"],
	);
	const signature = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(message));
	const hex = Array.from(new Uint8Array(signature))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");

	return `HMAC ${timestamp}:${hex}`;
}

interface AuthenticatedFetchOptions {
	readonly url: URL | RequestInfo;
	readonly options?: RequestInit;
	readonly getToken: GetAuthToken;
	readonly hmacSecret?: string;
	readonly fetchImpl?: (input: URL | RequestInfo, init?: RequestInit) => Promise<Response>;
}

function withAuthorization(options: RequestInit | undefined, authorization: string): RequestInit {
	const headers = new Headers(options?.headers);
	headers.set("Authorization", authorization);
	return { ...options, headers };
}

export async function authenticatedFetch({
	url,
	options,
	getToken,
	hmacSecret,
	fetchImpl = fetch,
}: AuthenticatedFetchOptions): Promise<Response> {
	const token = await getToken();
	if (token) {
		const response = await fetchImpl(url, withAuthorization(options, `Bearer ${token}`));
		if (response.status !== 401) return response;

		const refreshedToken = await getToken({ skipCache: true });
		if (!refreshedToken || refreshedToken === token) return response;
		return fetchImpl(url, withAuthorization(options, `Bearer ${refreshedToken}`));
	}

	if (!hmacSecret) {
		const response = await fetchImpl(url, options);
		if (response.status !== 401) return response;

		const refreshedToken = await getToken({ skipCache: true });
		if (!refreshedToken) return response;
		return fetchImpl(url, withAuthorization(options, `Bearer ${refreshedToken}`));
	}

	const method = options?.method ?? "GET";
	const body = typeof options?.body === "string" ? options.body : "";
	const rawUrl = url instanceof Request ? url.url : url.toString();
	const path = new URL(rawUrl, "http://localhost").pathname;
	const authorization = await signRequest(hmacSecret, method, path, body);

	return fetchImpl(url, withAuthorization(options, authorization));
}

/**
 * Create the tRPC client instance.
 *
 * Auth strategy:
 * - In production: Clerk session token sent as Bearer header
 * - In development: HMAC signing (if VITE_HMAC_SECRET is set) or no auth
 *
 * The API uses SuperJSON as a transformer for serializing Dates and other
 * complex types, so we must use the same transformer here.
 *
 * @param getToken - Clerk's getToken() function, returns a session JWT
 */
export function createTRPCClient(getToken: GetAuthToken) {
	const baseUrl = import.meta.env.VITE_API_URL ?? "";
	const hmacSecret = import.meta.env.VITE_HMAC_SECRET as string | undefined;

	return trpc.createClient({
		transformer: superjson,
		links: [
			httpBatchLink({
				url: `${baseUrl}/trpc`,
				maxURLLength: 2048,
				fetch(url, options) {
					return authenticatedFetch({ url, options, getToken, hmacSecret });
				},
			}),
		],
	});
}
